/**
 * O botão de Excel do relatório HTML (<c>Portal.exportar</c> / <c>data-exportar</c>): o relatório não baixa nada sozinho (o frame
 * não permite), então manda ao portal as colunas e as linhas do bloco, e é o portal que monta o .xlsx e baixa.
 *
 * O que chega vem do iframe, ou seja, de fora: tudo é conferido (tamanho, tipos, nome do arquivo) antes de virar planilha. Os dados
 * são os que a pessoa já consultou pelo mesmo caminho filtrado do servidor; aqui nada novo é buscado.
 */

export type TipoDeColuna = 'numero' | 'percentual' | 'moeda' | 'compacto' | 'data' | 'texto'

export interface ColunaExportada {
  apelido: string
  rotulo: string
  tipo: TipoDeColuna
  casas: number | null
}

export interface PedidoDeExportacao {
  nome: string
  bloco: string
  colunas: ColunaExportada[]
  linhas: unknown[][]
}

/** O mesmo teto que o servidor aplica a uma exportação, e o do Power BI (1 milhão de valores) para o total de células. */
export const MAX_LINHAS = 50_000
export const MAX_COLUNAS = 32
export const MAX_CELULAS = 1_000_000

const TIPOS: TipoDeColuna[] = ['numero', 'percentual', 'moeda', 'compacto', 'data', 'texto']
const MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const texto = (v: unknown, max: number) => (typeof v === 'string' ? v : '').slice(0, max)

/**
 * Confere o que o relatório mandou e devolve o pedido limpo. Lança um Error com a frase para o relatório mostrar ao lado do botão.
 */
export function conferirPedido(m: any): PedidoDeExportacao {
  if (!m || !Array.isArray(m.colunas) || !Array.isArray(m.linhas)) throw new Error('Pedido de exportação inválido')
  if (m.colunas.length === 0 || m.colunas.length > MAX_COLUNAS) throw new Error(`A exportação aceita de 1 a ${MAX_COLUNAS} colunas`)
  if (m.linhas.length === 0) throw new Error('Não há dados para exportar neste recorte')
  if (m.linhas.length > MAX_LINHAS) throw new Error(`A exportação aceita até ${MAX_LINHAS.toLocaleString('pt-BR')} linhas`)
  if (m.linhas.length * m.colunas.length > MAX_CELULAS) throw new Error('Há dados demais para uma planilha só: use filtros para reduzir o recorte')

  const colunas: ColunaExportada[] = m.colunas.map((c: any, i: number) => {
    const tipo = TIPOS.includes(c?.tipo) ? (c.tipo as TipoDeColuna) : 'texto'
    const casas = typeof c?.casas === 'number' && Number.isInteger(c.casas) && c.casas >= 0 && c.casas <= 6 ? c.casas : null
    return { apelido: texto(c?.apelido, 80), rotulo: texto(c?.rotulo, 120) || texto(c?.apelido, 80) || `Coluna ${i + 1}`, tipo, casas }
  })

  const linhas: unknown[][] = m.linhas.map((l: unknown) => {
    if (!Array.isArray(l) || l.length !== colunas.length) throw new Error('Pedido de exportação inválido')
    return l
  })

  return { nome: texto(m.nome, 200), bloco: texto(m.bloco, 80), colunas, linhas }
}

/** O nome do arquivo: o do relatório sem acento nem símbolo, e a data. "Visitas por mês" vira "visitas-por-mes-2026-10-30.xlsx". */
export function nomeDoArquivo(nome: string, hoje: string = new Date().toISOString().slice(0, 10)): string {
  const slug = nome.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60)
  return `${slug || 'dados'}-${hoje}.xlsx`
}

/** O nome da aba: até 31 caracteres e sem os símbolos que o Excel não aceita. */
export const nomeDaAba = (nome: string) => nome.replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || 'Dados'

/** O formato de número do Excel para a coluna (o Excel mostra no idioma de quem abre). */
export function formatoDaColuna(c: ColunaExportada): string | null {
  const casas = (padrao: number) => (c.casas ?? padrao)
  const zeros = (n: number) => (n > 0 ? `.${'0'.repeat(n)}` : '')
  switch (c.tipo) {
    case 'numero': return `#,##0${zeros(casas(0))}`
    case 'compacto': return '#,##0'
    case 'percentual': return `0${zeros(casas(1))}%`
    case 'moeda': return `"R$" #,##0${zeros(casas(2))}`
    case 'data': return 'dd/mm/yyyy'
    default: return null
  }
}

/** O valor da célula: número fica número, data vira data de verdade, o resto vira texto; vazio fica vazio. */
export function valorDaCelula(c: ColunaExportada, v: unknown): string | number | Date | null {
  if (v === null || v === undefined || v === '') return null
  if (c.tipo === 'texto') return typeof v === 'string' ? v : String(v)
  if (c.tipo === 'data') {
    const d = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v))
    // Data em UTC à meia-noite: o Excel guarda dia, não hora, e o fuso do navegador não pode empurrar para o dia anterior.
    return d ? new Date(Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]))) : String(v)
  }
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : null
}

const larguraDaColuna = (c: ColunaExportada, p: PedidoDeExportacao, i: number) => {
  let max = c.rotulo.length
  for (let j = 0; j < Math.min(p.linhas.length, 200); j++) {
    const v = p.linhas[j][i]
    const t = v == null ? 0 : c.tipo === 'texto' ? String(v).length : c.tipo === 'data' ? 10 : 14
    if (t > max) max = t
  }
  return Math.min(50, Math.max(10, max + 2))
}

/** Monta o .xlsx: cabeçalho em negrito e fixo no alto, filtro nas colunas, formato de cada coluna e largura pelo conteúdo. */
export async function montarPlanilha(p: PedidoDeExportacao): Promise<ArrayBuffer> {
  // Carregada só quando alguém exporta: é uma biblioteca grande e quase ninguém precisa dela ao abrir um relatório.
  const modulo: any = await import('exceljs')
  const ExcelJS = modulo.default || modulo
  const livro = new ExcelJS.Workbook()
  livro.creator = 'Portal Trade Talentos'
  livro.created = new Date()
  const aba = livro.addWorksheet(nomeDaAba(p.nome))

  aba.columns = p.colunas.map((c, i) => ({ header: c.rotulo, key: `c${i}`, width: larguraDaColuna(c, p, i), style: { numFmt: formatoDaColuna(c) || undefined } }))
  p.linhas.forEach(l => aba.addRow(l.map((v, i) => valorDaCelula(p.colunas[i], v))))

  const cabecalho = aba.getRow(1)
  cabecalho.font = { bold: true }
  cabecalho.alignment = { vertical: 'middle' }
  aba.views = [{ state: 'frozen', ySplit: 1 }]
  aba.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: p.colunas.length } }

  return livro.xlsx.writeBuffer()
}

/** Entrega o arquivo ao navegador, que o baixa. Roda na página do portal: o relatório (iframe) não tem permissão para isso. */
export function baixarXlsx(arquivo: string, dados: ArrayBuffer) {
  const url = URL.createObjectURL(new Blob([dados], { type: MIME }))
  const a = document.createElement('a')
  a.href = url
  a.download = arquivo
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}

export interface Exportado {
  arquivo: string
  bloco: string
  linhas: number
}

/** Confere o pedido, monta a planilha e entrega para baixar. Falha com um Error de frase pronta para o relatório mostrar. */
export async function exportarParaExcel(
  m: unknown,
  baixar: (arquivo: string, dados: ArrayBuffer) => void = baixarXlsx,
  hoje?: string
): Promise<Exportado> {
  const pedido = conferirPedido(m)
  let dados: ArrayBuffer
  try {
    dados = await montarPlanilha(pedido)
  } catch {
    throw new Error('Não foi possível montar a planilha')
  }
  const arquivo = nomeDoArquivo(pedido.nome, hoje)
  baixar(arquivo, dados)
  return { arquivo, bloco: pedido.bloco, linhas: pedido.linhas.length }
}
